import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PrimaryButton, SecondaryButton } from '../../../components/Button'
import { SectionBreakerHorizontal, SubTitle } from '../../../components/Shared'
import { PayoutType } from './payoutTypes'
import { ManagedPaymentMethod } from '../../../util/Managed'
import Radom, { Customer, ExternalAccount, IPriceQuote, PayoutFeeEstimate, PayoutFundingCurrency, PayoutQuoteSide } from '../../../state/Radom'
import BalanceInput from './BalanceInput'
import { InputLabel } from '../../../components/Input'
import { formatCurrency } from '../../../util/Util'
import Dropdown, { DropdownItem } from '../../../components/Dropdown'
import ExternalAccountSelectorTag from './ExternalAccountSelectorTag'
import SummaryRow from './SummaryRow'
import DetailsRow from './DetailsRow'
import { Currency } from '../../../util/Currencies'
import Spinner from '../../../components/Spinner'
import { isCryptoExternalAccount, isEurIbanExternalAccount } from '../../../util/ExternalAccounts'

interface IPayoutDetailsProps {
  payoutType: PayoutType
  isHedged: boolean
  fundingCurrency: PayoutFundingCurrency
  payoutSide: PayoutQuoteSide
  setPayoutSide: (side: PayoutQuoteSide) => void
  selectedFundingMethod: ManagedPaymentMethod | undefined
  selectedPayoutMethod: ManagedPaymentMethod | undefined | Currency
  selectedCustomer: Customer | null
  onContinue: () => void
  onBack: () => void
  payoutAmount: string
  setPayoutAmount: (amount: string) => void
  chainName: string | undefined
  availableBalance: number
  decimals: number
  // External accounts for both fiat and crypto destination
  externalAccounts: ExternalAccount[]
  selectedExternalAccount?: ExternalAccount
  setSelectedExternalAccount: (account: ExternalAccount) => void
  // Fee state (managed by parent)
  feeEstimate: PayoutFeeEstimate | null
  setFeeEstimate: (fee: PayoutFeeEstimate | null) => void
}

const PayoutDetails = ({
  payoutType,
  isHedged,
  fundingCurrency,
  payoutSide,
  setPayoutSide,
  selectedFundingMethod,
  selectedPayoutMethod,
  selectedCustomer,
  onContinue,
  onBack,
  payoutAmount,
  setPayoutAmount,
  chainName,
  availableBalance,
  decimals,
  externalAccounts,
  selectedExternalAccount,
  setSelectedExternalAccount,
  feeEstimate,
  setFeeEstimate
}: IPayoutDetailsProps): JSX.Element => {
  const isFiat = payoutType === PayoutType.Fiat

  const hedgedDestinationNetwork = isHedged && selectedPayoutMethod && !('icon' in selectedPayoutMethod)
    ? selectedPayoutMethod.hostChain?.name ?? selectedPayoutMethod.name
    : undefined

  // Filter external accounts by type: fiat accounts for fiat payouts, crypto for crypto
  const filteredExternalAccounts = useMemo(() =>
    externalAccounts.filter(a => isFiat
      ? isEurIbanExternalAccount(a)
      : a.active && isCryptoExternalAccount(a) &&
        (!hedgedDestinationNetwork || a.crypto?.network === hedgedDestinationNetwork)),
  [externalAccounts, isFiat, hedgedDestinationNetwork]
  )

  const payoutTicker = (isFiat || isHedged) && selectedPayoutMethod && 'ticker' in selectedPayoutMethod
    ? selectedPayoutMethod.ticker
    : selectedFundingMethod?.ticker || ''

  // Unit the payout is funded in (fiat payouts fund from USDC/USDT balances).
  const fundingTicker = isHedged ? fundingCurrency : (selectedFundingMethod?.ticker || 'USDC')

  const [isLoadingFee, setIsLoadingFee] = useState(false)
  const [priceQuote, setPriceQuote] = useState<IPriceQuote | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeExternalAccountDropdown = useRef<() => void>()

  // For fiat payouts, fetch funding-asset → payout-currency rate; for crypto fetch USDC → USD
  const quoteToCurrency = isFiat && selectedPayoutMethod && 'ticker' in selectedPayoutMethod
    ? selectedPayoutMethod.ticker.toUpperCase()
    : 'USD'
  const quoteFromTicker = isFiat ? fundingTicker : 'USDC'

  useEffect(() => {
    if (isHedged) return
    Radom.getPriceQuotes([quoteFromTicker], [quoteToCurrency])
      .then(quotes => {
        if (quotes.length > 0) {
          setPriceQuote(quotes[0])
        }
      })
      .catch(err => console.error('Failed to fetch price quote', err))
  }, [quoteToCurrency, quoteFromTicker, isHedged])

  const fetchFeeEstimate = useCallback(async (amount: string) => {
    if (!amount || Number(amount) <= 0 || isHedged) {
      setFeeEstimate(null)
      return
    }

    setIsLoadingFee(true)
    try {
      if (isFiat) {
        // Fiat fee estimation — currency must be UPPERCASE to match backend enum
        const currency = selectedPayoutMethod && 'ticker' in selectedPayoutMethod
          ? selectedPayoutMethod.ticker.toUpperCase()
          : 'EUR'
        const accountType = selectedExternalAccount?.accountOwnerType || selectedCustomer?.accountType || 'individual'
        const fiatFundingCurrency: PayoutFundingCurrency =
          selectedFundingMethod?.ticker === 'USDT' ? 'USDT' : 'USDC'

        const fee = await Radom.getPayoutFee({
          currency,
          amount,
          accountType,
          fundingCurrency: fiatFundingCurrency
        })
        setFeeEstimate(fee)
      } else {
        // Crypto fee estimation
        if (selectedFundingMethod) {
          const network = selectedFundingMethod.hostChain?.name || selectedFundingMethod.name
          const fee = await Radom.getPayoutFee({
            currency: 'USD',
            amount,
            network,
            token: selectedFundingMethod.ticker
          })
          setFeeEstimate(fee)
        }
      }
    } catch (err) {
      console.error('Failed to fetch payout fee', err)
      setFeeEstimate(null)
    } finally {
      setIsLoadingFee(false)
    }
  }, [isFiat, isHedged, selectedPayoutMethod, selectedExternalAccount,
    selectedCustomer, selectedFundingMethod, setFeeEstimate])

  // Debounced fee fetch when amount changes
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      fetchFeeEstimate(payoutAmount)
    }, 500)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [payoutAmount, fetchFeeEstimate])

  const onSelectExternalAccount = useCallback((account: ExternalAccount) => {
    setSelectedExternalAccount(account)
    closeExternalAccountDropdown.current?.()
  }, [setSelectedExternalAccount])

  const formatFeeAmount = (value: number): string => {
    if (value === 0) return '0'
    return Number(value).toFixed(2)
  }

  const formatPercent = (value: number): string => {
    return `${(Number(value) * 100).toFixed(2)}%`
  }

  const formatUsdcTotal = (value: number): string => {
    if (value === 0) return '0'
    return value.toFixed(6)
  }

  const hasAmount = payoutAmount && Number(payoutAmount) > 0

  const isSourceSide = isHedged && payoutSide === 'from'

  const recipientReceives = feeEstimate
    ? `${formatFeeAmount(feeEstimate.estimatedRecipientAmount)} ${payoutTicker}`
    : isHedged
      ? isSourceSide
        ? 'Locked on next step'
        : hasAmount
          ? `${payoutAmount} ${payoutTicker}`
          : `0 ${payoutTicker}`
      : hasAmount
        ? `${payoutAmount} ${payoutTicker}`
        : `0 ${payoutTicker}`

  const totalUsdcRequired = useMemo((): number | null => {
    if (isHedged) {
      if (!hasAmount || !isSourceSide) return null
      return Number(payoutAmount)
    }
    if (!feeEstimate || !hasAmount) return null
    const fee = Number(feeEstimate.estimatedFeeUsd) || 0
    if (isFiat) {
      if (feeEstimate.estimatedTotalDebitUsd !== undefined && feeEstimate.estimatedTotalDebitUsd !== null) {
        return Number(feeEstimate.estimatedTotalDebitUsd)
      }

      if (!priceQuote) return null
      const rate = Number(priceQuote.value)
      if (!rate || isNaN(rate)) return null
      const payoutUsdc = Number(payoutAmount) / rate
      return payoutUsdc + fee
    }

    const networkFee = Number(feeEstimate.networkFeeUsd) || 0
    return Number(payoutAmount) + networkFee
  }, [feeEstimate, hasAmount, isFiat, isHedged, isSourceSide, priceQuote, payoutAmount])

  const exceedsAvailableBalance = isHedged
    ? isSourceSide && hasAmount && Number(payoutAmount) - availableBalance > 0.000001
    : totalUsdcRequired !== null && totalUsdcRequired - availableBalance > 0.000001
  const cannotContinue = isHedged
    ? (!payoutAmount ||
      +payoutAmount <= 0 ||
      !selectedExternalAccount ||
      exceedsAvailableBalance)
    : (!payoutAmount ||
      +payoutAmount <= 0 ||
      !selectedExternalAccount ||
      isLoadingFee ||
      !feeEstimate ||
      totalUsdcRequired === null ||
      exceedsAvailableBalance)

  const customerFacingFiatRate = useMemo((): number | null => {
    if (!isFiat || !feeEstimate || !hasAmount) return null
    if (feeEstimate.exchangeRate !== undefined && feeEstimate.exchangeRate !== null) {
      return Number(feeEstimate.exchangeRate)
    }
    if (!feeEstimate.estimatedFundingAmountUsd) return null
    const fundingAmount = Number(feeEstimate.estimatedFundingAmountUsd)
    const recipientAmount = Number(payoutAmount)
    if (!fundingAmount || !recipientAmount || isNaN(fundingAmount) || isNaN(recipientAmount)) return null
    return recipientAmount / fundingAmount
  }, [feeEstimate, hasAmount, isFiat, payoutAmount])

  const exchangeRateDisplay = isFiat
    ? customerFacingFiatRate
      ? `1 ${fundingTicker} = ${customerFacingFiatRate.toFixed(4)} ${quoteToCurrency}`
      : 'Calculating...'
    : priceQuote
      ? `1 USDC = $${Number(priceQuote.value).toFixed(3)} USD`
      : '1 USDC ≈ $1.00 USD'

  return (
    <div style={{
      maxWidth: 560,
      width: '100%',
      display: 'flex',
      padding: 28,
      flexDirection: 'column',
      borderRadius: 22,
      border: '1px solid var(--surface-pressed, #edf1f7)',
      boxShadow: '0 10px 28px rgba(15, 23, 42, 0.035)',
      gap: 22,
      background: 'var(--surface, #ffffff)'
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <h1 style={{ fontSize: 18.5, fontWeight: 500, lineHeight: '24px', margin: 0 }}>Enter Payout Details</h1>
        <SubTitle style={{ fontSize: 13.5, lineHeight: '20px' }}>Sending to {
          selectedCustomer?.name && selectedCustomer.name.trim().length > 0
            ? selectedCustomer.name
            : selectedCustomer?.email}</SubTitle>
      </div>
      <div style={{
        alignItems: 'center',
        background: 'var(--surface-subtle, #fbfcff)',
        border: '1px solid var(--surface-pressed, #edf1f7)',
        borderRadius: 16,
        display: 'flex',
        gap: 12,
        justifyContent: 'space-between',
        padding: '12px 14px'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ color: 'var(--text-soft, #7d8492)', fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Recipient contact</span>
          <span style={{ color: 'var(--ink, #20242d)', fontSize: 14, fontWeight: 500 }}>
            {selectedCustomer?.name || selectedCustomer?.companyName || selectedCustomer?.email || '-'}
          </span>
        </div>
      </div>
      <SectionBreakerHorizontal />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }} >
        <InputLabel style={{ opacity: 1 }}>Payout Amount</InputLabel>
        {isHedged && (
          <div style={{
            display: 'flex',
            background: 'var(--surface-hover, #f4f5f8)',
            border: '1px solid var(--border-soft, #eceff3)',
            borderRadius: 10,
            padding: 3,
            gap: 3,
            alignSelf: 'flex-start'
          }}>
            {([
              { side: 'to' as PayoutQuoteSide, label: `Recipient receives (${payoutTicker})` },
              { side: 'from' as PayoutQuoteSide, label: `You pay (${fundingCurrency})` }
            ]).map(({ side, label }) => (
              <button
                key={side}
                type='button'
                onClick={() => setPayoutSide(side)}
                style={{
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: 8,
                  padding: '6px 12px',
                  fontSize: 12.5,
                  fontWeight: 500,
                  background: payoutSide === side ? '#fff' : 'transparent',
                  color: payoutSide === side ? 'var(--ink, #20242d)' : 'var(--icon-muted, #747894)',
                  boxShadow: payoutSide === side ? '0 1px 3px rgba(15, 23, 42, 0.08)' : 'none'
                }}>
                {label}
              </button>
            ))}
          </div>
        )}
        <BalanceInput
          amount={payoutAmount}
          onAmount={setPayoutAmount}
          availableBalance={availableBalance}
          ticker={isSourceSide ? fundingCurrency : (isFiat || isHedged) ? payoutTicker : (selectedFundingMethod?.ticker ?? '')}
          showMax={(!isFiat && !isHedged) || isSourceSide} />
        <span style={{
          color: '#808080',
          fontSize: 12.5,
          fontWeight: 400,
          lineHeight: '16px'
        }}>
          Available balance: <span style={{ color: 'var(--ink, #20242d)', fontWeight: 500 }}>{formatCurrency(availableBalance, '', decimals)}</span> {isHedged ? fundingCurrency : selectedFundingMethod?.ticker} on {chainName}
        </span>
      </div>

      {/* Destination section */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }} >
        <InputLabel>{isFiat ? 'Destination Bank Account' : 'Select Destination Address'}</InputLabel>

        {/* Both fiat and crypto use external accounts as destination */}
        <Dropdown
          containerStyle={{ flex: 1 }}
          onCloseFn={(fn) => { closeExternalAccountDropdown.current = fn }}
          selectedContent={
            selectedExternalAccount
              ? <ExternalAccountSelectorTag account={selectedExternalAccount} />
              : <div style={{ display: 'flex', alignItems: 'center', height: 36, opacity: 0.5, fontSize: 12, fontWeight: 500, paddingLeft: 6 }}>
                {isFiat ? 'Select bank account' : 'Select wallet address'}
              </div>
          }
          dropdownContent={
            <div>
              {filteredExternalAccounts.map((account) => (
                <DropdownItem key={account.id} onClick={() => onSelectExternalAccount(account) }>
                  <ExternalAccountSelectorTag account={account} />
                </DropdownItem>
              ))}
              {filteredExternalAccounts.length === 0 && (
                <div style={{ padding: '12px 16px', fontSize: 13, color: 'var(--icon-muted, #747894)', textAlign: 'center' }}>
                  No {isFiat ? 'bank' : 'crypto'} accounts found
                </div>
              )}
            </div>
          }
        />
      </div>

      {/* Fee breakdown section — always show when amount > 0 */}
      <SectionBreakerHorizontal/>
      {hasAmount
        ? (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {!isHedged && (
                <>
                  <DetailsRow title='Asset Exchange rate' value={exchangeRateDisplay} />
                  <DetailsRow title='Estimated arrival' value='Instant' />
                </>
              )}
              {isHedged && (
                <span style={{ fontSize: 13, color: 'var(--icon-muted, #747894)', lineHeight: '18px' }}>
                  {isSourceSide
                    ? `${payoutAmount} ${fundingCurrency} will be deducted. The recipient amount is locked on the next step.`
                    : `Recipient receives ${payoutAmount} ${payoutTicker}. The exact cost is locked on the next step.`}
                </span>
              )}
              {!isHedged && (isLoadingFee
                ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 0' }}>
                    <Spinner />
                    <span style={{ marginLeft: 8, fontSize: 13, color: 'var(--icon-muted, #747894)' }}>Calculating fees...</span>
                  </div>
                )
                : feeEstimate
                  ? (
                    <>
                      {isFiat
                        ? (
                          <>
                            {feeEstimate.flatFeeUsd > 0 && (
                              <DetailsRow title='Flat Fee' value={`${formatFeeAmount(feeEstimate.flatFeeUsd)} ${fundingTicker}`} />
                            )}
                            {feeEstimate.minimumFeeUsd !== undefined && (
                              <DetailsRow title='Minimum Payout Fee' value={`${formatFeeAmount(feeEstimate.minimumFeeUsd)} ${fundingTicker}`} />
                            )}
                            {feeEstimate.feePercent > 0 && (
                              <DetailsRow title='Payout Fee Rate' value={formatPercent(feeEstimate.feePercent)} />
                            )}
                            <DetailsRow title='Payout Fee' value={`${formatFeeAmount(feeEstimate.estimatedFeeUsd)} ${fundingTicker}`} />
                          </>
                        )
                        : (
                          <>
                            {feeEstimate.networkFeeUsd !== undefined && feeEstimate.networkFeeUsd !== null && (
                              <DetailsRow title='Network Fee' value={`${formatFeeAmount(feeEstimate.networkFeeUsd)} ${payoutTicker}`} />
                            )}
                          </>
                        )
                      }
                    </>
                  )
                  : (
                    <>
                      {isFiat
                        ? (
                          <>
                            <DetailsRow title='Offramp Fee' value='--' />
                            <DetailsRow title='Payment Rail Fee' value='--' />
                          </>
                        )
                        : <DetailsRow title='Network Fee' value='--' />
                      }
                    </>
                  ))
              }
            </div>
            <div>
              <SectionBreakerHorizontal/>
              {isLoadingFee
                ? <SummaryRow title="Recipient Receives" value="Calculating..." />
                : <SummaryRow title="Recipient Receives" value={recipientReceives} />
              }
              {isLoadingFee
                ? <SummaryRow title="You pay" value="Calculating..." />
                : isHedged
                  ? <SummaryRow
                    title="You pay"
                    value={isSourceSide && totalUsdcRequired !== null
                      ? `${formatUsdcTotal(totalUsdcRequired)} ${fundingCurrency}`
                      : 'Locked on next step'} />
                  : totalUsdcRequired !== null
                    ? <SummaryRow
                      title="Total deducted from balance"
                      value={`${formatUsdcTotal(totalUsdcRequired)} ${fundingTicker}`} />
                    : <SummaryRow title="Total deducted from balance" value={`0 ${fundingTicker}`} />
              }
            </div>
          </>
        )
        : <SummaryRow title="Total" value={`0 ${payoutTicker}`} />
      }

      <div style={{ display: 'flex', width: '100%', gap: 12 }}>
        <SecondaryButton onClick={onBack} style={{ width: '100%' }} >
            Back
        </SecondaryButton>

        <PrimaryButton
          onClick={onContinue}
          disabled={cannotContinue}
          style={{ width: '100%' }}
        >
            Continue
        </PrimaryButton>
      </div>
    </div>

  )
}

export default PayoutDetails
